import {
  createContext,
  useContext,
  PropsWithChildren,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { Updater, useImmer } from 'use-immer';
import { produce } from 'immer';
import { AxiosError } from 'axios';
import { Datasource, Datasource_Preset } from '@/types/api';
import {
  getUnpublishedChanges,
  getUpgradeStatusObject,
} from '@/lib/datasource/uiStatus';
import {
  applyUpgradePreset,
  getInitialUpgradeStatus,
  getDiffStatus,
  getTableDiffStatus,
  getUpgradeDiff,
} from '@/lib/datasource/upgrade/datasourceUpgradeHelper';
import { getBronzeChanges } from '@/lib/datasource/upgrade/bronze/datasourceUpgradeBronzeHelper';
import { getPreTransformChanges } from '@/lib/datasource/upgrade/preTransform/datasourceUpgradePreTransformHelper';
import { getSilverChanges } from '@/lib/datasource/upgrade/silver/datasourceUpgradeSilverHelper';
import { getGoldChanges } from '@/lib/datasource/upgrade/gold/datasourceUpgradeGoldHelper';
import {
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeItemDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeStepDiff,
  DatasourceUpgradeMedallionDiff,
  DatasourceUpgradeDiff,
} from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import {
  isFieldSpecChange,
  isMultiValueFieldChange,
  isPreserveUnreferencedColumnsChange,
  isStringFieldChange,
  isTableChange,
} from '@/lib/datasource/upgrade/utils/datasourceUpgradeUtils';
import { useUpdateDataSource } from '@/hooks/core/useUpdateDataSource';
import { useGetDatasourcePresetVersions } from '@/hooks/content/useGetDatasourcePresetVersions';
import {
  clearDatasourceUpgrade,
  updateDatasourceUpgrade,
} from '@/lib/datasource/upgrade/actions/datasourceUpgradeActions';
import { useOptimisticUpdateGetDataSource } from '@/hooks/core/useGetDataSource';

interface DatasourceUpgradeContextType {
  isUpgrading: boolean;
  applyUpgradeStatusChange: (
    recipe: (draft: DatasourceUpgradeStatus) => void,
  ) => void;
  upgradeDiff: DatasourceUpgradeDiff | undefined;
  upgradeStatus: DatasourceUpgradeStatus | undefined;
  bronze: DatasourceUpgradeStepDiff;
  preTransform: DatasourceUpgradeStepDiff;
  silver: DatasourceUpgradeMedallionDiff;
  gold: DatasourceUpgradeMedallionDiff;
  datasource: Datasource;
  setDatasource: Updater<Datasource>;
  setFieldStatus: (
    fieldStates: DatasourceUpgradeItemDiff | DatasourceUpgradeItemDiff[],
    status: DatasourceUpgradeFieldStatus,
  ) => void;
  currentDiffIndex: number;
  setCurrentDiff: (diff: DatasourceUpgradeItemDiff) => void;
  currentDiff: DatasourceUpgradeItemDiff;
  nextDiff: () => void;
  previousDiff: () => void;
  diffs: DatasourceUpgradeItemDiff[];
  acceptChanges: (fieldDiff: DatasourceUpgradeItemDiff) => void;
  rejectChanges: (fieldDiff: DatasourceUpgradeItemDiff) => void;
  presetUpgradeVersion: number;
  presetName: string;
  setChanges: (params: {
    fieldDiff: DatasourceUpgradeItemDiff;
    value: string | string[] | undefined;
  }) => void;
  hasUnappliedChanges: boolean;
  hasConflictedChanges: boolean;
  hasUnconfirmedChanges: boolean;
  nextUnappliedChange: () => void;
  save: (params?: {
    onSuccess?: () => void;
    onError?: (error: AxiosError) => void;
  }) => void;
  publish: (params?: {
    onSuccess?: () => void;
    onError?: (error: AxiosError) => void;
  }) => void;
  abort: (params?: {
    onSuccess?: () => void;
    onError?: (error: AxiosError) => void;
  }) => void;
  title: string;
  description: string;
  isSaving: boolean;
  isPublishing: boolean;
}

const DatasourceUpgradeContext = createContext<
  DatasourceUpgradeContextType | undefined
>(undefined);

export type DatasourceUpgradeProviderProps = {
  currentPreset: Datasource_Preset;
  upgradePreset: Datasource_Preset;
  datasource: Datasource;
};

export const DatasourceUpgradeProvider = ({
  children,
  currentPreset,
  upgradePreset,
  datasource: datasourceInitial,
}: DatasourceUpgradeProviderProps & PropsWithChildren) => {
  const { mutateAsync: updateDatasource } = useUpdateDataSource();
  const { data: presetVersions } = useGetDatasourcePresetVersions({
    name: datasourceInitial.spec.usePreset || '',
  });
  const optimisticUpdate = useOptimisticUpdateGetDataSource();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // this datasource is the version used to display the review page. The diff in preset
  // versions is overlaid on top of this datasource. As the user makes decisions on the
  // upgrade, the datasource is updated and the upgrade status is updated.
  const [datasource, setDatasource] = useImmer<Datasource>(
    getUnpublishedChanges(datasourceInitial) ||
      applyUpgradePreset(datasourceInitial, currentPreset, upgradePreset),
  );

  // this is the status of the upgrade. The upgrade state is downstream of this.
  // If there is an upgrade in progress we load it via getUpgradeStatus. If this is a
  // new upgrade, we load the initial status via getInitialUpgradeStatus.
  const [upgradeStatus, setUpgradeStatus] = useImmer<
    DatasourceUpgradeStatus | undefined
  >(
    getUpgradeStatusObject(datasourceInitial) ||
      getInitialUpgradeStatus(datasourceInitial, currentPreset, upgradePreset),
  );

  /**
   * This is used to update the upgrade status.
   * It is a callback that takes a recipe function that updates the draft. e.g.
   *     applyUpgradeStatusChange((draft) => {
   *       draft.spec.autoloader.format = 'confirmed';
   *     });
   */
  const applyUpgradeStatusChange = useCallback(
    (recipe: (draft: DatasourceUpgradeStatus) => void) => {
      if (upgradeStatus) {
        setUpgradeStatus(produce(upgradeStatus, recipe));
      }
    },
    [upgradeStatus, setUpgradeStatus],
  );

  const applyUpgradeDatasourceChange = useCallback(
    (recipe: (draft: Datasource) => void) => {
      setDatasource(produce(datasource, recipe));
    },
    [datasource, setDatasource],
  );

  const setFieldStatus = useCallback(
    (
      fieldDiffs: DatasourceUpgradeItemDiff | DatasourceUpgradeItemDiff[],
      status: DatasourceUpgradeFieldStatus,
    ) => {
      applyUpgradeStatusChange((draft) => {
        if (Array.isArray(fieldDiffs)) {
          fieldDiffs.forEach((fieldState) => {
            fieldState.updateStatus({ draft, status });
          });
        } else {
          fieldDiffs.updateStatus({ draft, status });
        }
      });
    },
    [applyUpgradeStatusChange],
  );

  const acceptChanges = useCallback(
    (fieldDiff: DatasourceUpgradeItemDiff) => {
      applyUpgradeDatasourceChange((draft) => {
        if (isFieldSpecChange(fieldDiff)) {
          fieldDiff.acceptChanges({ diff: fieldDiff, datasource: draft });
          return;
        }
        if (isPreserveUnreferencedColumnsChange(fieldDiff)) {
          fieldDiff.setChanges({
            diff: fieldDiff,
            datasource: draft,
            value: fieldDiff.upgradePresetValue,
          });
          return;
        }

        if (isTableChange(fieldDiff)) {
          fieldDiff.acceptChanges({ diff: fieldDiff, datasource: draft });
          return;
        }

        throw new Error(
          `Unhandled diff type for acceptChanges ${fieldDiff.type}`,
        );
      });
    },
    [applyUpgradeDatasourceChange],
  );

  const rejectChanges = useCallback(
    (fieldDiff: DatasourceUpgradeItemDiff) => {
      applyUpgradeDatasourceChange((draft) => {
        if (isFieldSpecChange(fieldDiff)) {
          fieldDiff.rejectChanges({ diff: fieldDiff, datasource: draft });
          return;
        }

        if (isPreserveUnreferencedColumnsChange(fieldDiff)) {
          fieldDiff.setChanges({
            diff: fieldDiff,
            datasource: draft,
            value: fieldDiff.currentValue,
          });
          return;
        }

        if (isTableChange(fieldDiff)) {
          fieldDiff.rejectChanges({ diff: fieldDiff, datasource: draft });
          return;
        }

        throw new Error(`Unhandled diff type for setChanges ${fieldDiff.type}`);
      });
    },
    [applyUpgradeDatasourceChange],
  );

  const setChanges = useCallback(
    ({
      fieldDiff,
      value,
    }: {
      fieldDiff: DatasourceUpgradeItemDiff;
      value: string | string[] | undefined;
    }) => {
      applyUpgradeDatasourceChange((draft) => {
        if (isStringFieldChange(fieldDiff) && typeof value === 'string') {
          fieldDiff.setChanges({ diff: fieldDiff, datasource: draft, value });
          return;
        }

        if (isMultiValueFieldChange(fieldDiff) && Array.isArray(value)) {
          fieldDiff.setChanges({ diff: fieldDiff, datasource: draft, value });
          return;
        }

        throw new Error(`Unhandled diff type for setChanges ${fieldDiff.type}`);
      });
    },
    [applyUpgradeDatasourceChange],
  );

  // we need to use the initial datasource here because we want to be able to compare
  // the current values with the two preset versions
  const upgradeDiff: DatasourceUpgradeDiff | undefined = useMemo(() => {
    if (!upgradeStatus) {
      return undefined;
    }
    return getUpgradeDiff(
      datasourceInitial,
      datasource,
      currentPreset,
      upgradePreset,
      upgradeStatus,
    );
  }, [
    currentPreset,
    upgradePreset,
    datasourceInitial,
    upgradeStatus,
    datasource,
  ]);

  const bronzeChanges = useMemo(() => {
    return getBronzeChanges(upgradeDiff);
  }, [upgradeDiff]);

  const preTransformChanges = useMemo(() => {
    return getPreTransformChanges(upgradeDiff);
  }, [upgradeDiff]);

  const silverChanges = useMemo(() => {
    return getSilverChanges(upgradeDiff);
  }, [upgradeDiff]);

  const goldChanges = useMemo(() => {
    return getGoldChanges(upgradeDiff);
  }, [upgradeDiff]);

  const bronze = useMemo(() => {
    return {
      changes: bronzeChanges,
      status: getDiffStatus(bronzeChanges),
    };
  }, [bronzeChanges]);

  const preTransform = useMemo(() => {
    return {
      changes: preTransformChanges,
      status: getDiffStatus(preTransformChanges),
    };
  }, [preTransformChanges]);

  const silver = useMemo(() => {
    return {
      tables: silverChanges,
      status: getTableDiffStatus(silverChanges),
    };
  }, [silverChanges]);

  const gold = useMemo(() => {
    return {
      tables: goldChanges,
      status: getTableDiffStatus(goldChanges),
    };
  }, [goldChanges]);

  const diffs = useMemo(() => {
    return [
      ...bronze.changes,
      ...preTransform.changes,
      ...silver.tables.flatMap((table) => table.changes),
      ...gold.tables.flatMap((table) => table.changes),
    ];
  }, [bronze, preTransform, silver, gold]);

  const [currentDiffIndex, setCurrentDiffIndex] = useState(
    Math.max(
      0,
      diffs.findIndex((diff) => diff.status !== 'confirmed'),
    ),
  );

  const nextDiff = useCallback(() => {
    setCurrentDiffIndex(currentDiffIndex + 1);
  }, [currentDiffIndex]);

  const previousDiff = useCallback(() => {
    setCurrentDiffIndex(currentDiffIndex - 1);
  }, [currentDiffIndex]);

  const currentDiff = useMemo(() => {
    return diffs[currentDiffIndex];
  }, [diffs, currentDiffIndex]);

  const setCurrentDiff = useCallback(
    (diff: DatasourceUpgradeItemDiff) => {
      setCurrentDiffIndex(diffs.indexOf(diff));
    },
    [diffs],
  );

  const hasUnconfirmedChanges = useMemo(() => {
    return diffs.some((diff) => diff.status !== 'confirmed');
  }, [diffs]);

  const hasUnappliedChanges = useMemo(() => {
    return diffs.some((diff) => diff.status === 'unapplied');
  }, [diffs]);

  const hasConflictedChanges = useMemo(() => {
    return diffs.some((diff) => diff.status === 'conflicted');
  }, [diffs]);

  const nextUnappliedChange = useCallback(() => {
    // Start searching from the current index + 1
    for (let i = currentDiffIndex + 1; i < diffs.length; i++) {
      if (diffs[i].status !== 'confirmed') {
        setCurrentDiffIndex(i);
        return;
      }
    }

    // If not found, search from the beginning up to the current index
    for (let i = 0; i < currentDiffIndex; i++) {
      if (diffs[i].status !== 'confirmed') {
        setCurrentDiffIndex(i);
        return;
      }
    }
  }, [diffs, currentDiffIndex]);

  // save the changes to the datasource uiStatus JSON blob
  const save = useCallback(
    ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: (error: AxiosError) => void;
    } = {}) => {
      setIsSaving(true);
      const newDatasource = updateDatasourceUpgrade(
        datasource,
        upgradeStatus,
        upgradePreset,
        datasourceInitial,
      );

      updateDatasource(
        {
          data: newDatasource,
          name: newDatasource.metadata?.name || '',
        },
        {
          onSuccess: async ({ data }) => {
            optimisticUpdate({
              data,
              name: newDatasource.metadata?.name || '',
            });
            onSuccess?.();
            setIsSaving(false);
          },
          onError: async ({ error }) => {
            onError?.(error);
            setIsSaving(false);
          },
          toasts: {
            error: 'Unable to save preset upgrade',
          },
        },
      );
    },
    [
      upgradeStatus,
      upgradePreset,
      datasourceInitial,
      datasource,
      updateDatasource,
      optimisticUpdate,
    ],
  );

  // publish the changes to the datasource
  const publish = useCallback(
    ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: (error: AxiosError) => void;
    } = {}) => {
      setIsPublishing(true);
      const newDatasource = clearDatasourceUpgrade(
        datasource,
        datasourceInitial,
      );

      updateDatasource(
        {
          data: newDatasource,
          name: newDatasource.metadata?.name || '',
        },
        {
          onSuccess: async ({ data }) => {
            optimisticUpdate({
              data,
              name: newDatasource.metadata?.name || '',
            });
            onSuccess?.();
            setIsPublishing(false);
          },
          onError: async ({ error }) => {
            onError?.(error);
            setIsPublishing(false);
          },
          toasts: {
            success: 'Preset upgrade complete!',
            error: 'Unable to publish preset upgrade',
          },
        },
      );
    },
    [datasourceInitial, datasource, updateDatasource, optimisticUpdate],
  );

  const abort = useCallback(
    ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: (error: AxiosError) => void;
    } = {}) => {
      const newDatasource = clearDatasourceUpgrade(
        datasourceInitial,
        datasourceInitial,
      );

      updateDatasource(
        {
          data: newDatasource,
          name: newDatasource.metadata?.name || '',
        },
        {
          onSuccess: async () => {
            onSuccess?.();
          },
          onError: async ({ error }) => {
            onError?.(error);
          },
          toasts: {
            success: 'Preset upgrade aborted!',
            error: 'Unable to abort preset upgrade',
          },
        },
      );
    },
    [datasourceInitial, updateDatasource],
  );

  const title = useMemo(() => {
    return `${upgradePreset.name}: Update v${upgradePreset.versionNumber} by ${upgradePreset.author}`;
  }, [upgradePreset]);

  const description = useMemo(() => {
    return (
      presetVersions?.versions.find(
        (version) => version.versionNumber === upgradePreset.versionNumber,
      )?.changes || ''
    );
  }, [presetVersions, upgradePreset]);

  return (
    <DatasourceUpgradeContext.Provider
      value={{
        hasUnappliedChanges,
        hasConflictedChanges,
        hasUnconfirmedChanges,
        isUpgrading: !!upgradePreset,
        applyUpgradeStatusChange,
        upgradeDiff,
        upgradeStatus,
        bronze,
        preTransform,
        silver,
        gold,
        datasource,
        setDatasource,
        setFieldStatus,
        currentDiffIndex,
        setCurrentDiff,
        currentDiff,
        nextDiff,
        previousDiff,
        diffs,
        acceptChanges,
        rejectChanges,
        presetUpgradeVersion: upgradePreset.versionNumber!,
        presetName: upgradePreset.name!,
        setChanges,
        nextUnappliedChange,
        save,
        publish,
        abort,
        title,
        description,
        isSaving,
        isPublishing,
      }}
    >
      {children}
    </DatasourceUpgradeContext.Provider>
  );
};

export const useDatasourceUpgrade = (): DatasourceUpgradeContextType => {
  const context = useContext(DatasourceUpgradeContext);
  if (context === undefined) {
    throw new Error(
      'useDatasourceUpgradeContext must be used within a DatasourceUpgradeProvider',
    );
  }
  return context;
};
